import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { fetchNotifications, markNotificationRead, markAllNotificationsRead } from '../../utils/api/notificationApi';
import { Menu, Search, Bell, CheckCheck } from 'lucide-react';

const ReporterTopbar = ({ title, onToggleSidebar }) => {
  const { user } = useAuth();
  const { language, toggleLanguage } = useLanguage();
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const res = await fetchNotifications();
      const data = res.data || res || [];
      setNotifications(Array.isArray(data) ? data : []);
      setUnreadCount(res.unreadCount || 0);
    } catch (err) {
      setNotifications([]);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {}
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {}
  };

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'RP';

  return (
    <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-char-100">
      <div className="flex items-center justify-between px-4 lg:px-6 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onToggleSidebar}
            className="rounded-xl p-2 text-char-500 hover:bg-char-50 transition-colors lg:hidden"
          >
            <Menu size={20} />
          </button>
          <h1 className="text-lg font-extrabold text-char-900">{title}</h1>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden sm:flex items-center gap-2 rounded-xl bg-char-50 px-3 py-2 text-char-400 focus-within:bg-white focus-within:ring-2 focus-within:ring-primary-200 focus-within:text-char-600 transition-all">
            <Search size={16} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={language === 'ne' ? 'खोज्नुहोस्...' : 'Search...'}
              className="w-40 bg-transparent text-sm text-char-900 outline-none placeholder:text-char-400 lg:w-52"
            />
          </div>

          <button
            onClick={toggleLanguage}
            className="flex items-center gap-1.5 rounded-xl border border-char-100 px-3 py-2 text-xs font-bold uppercase tracking-wider text-char-600 hover:bg-char-50 transition-colors"
          >
            {language === 'ne' ? 'ने' : 'EN'}
          </button>

          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative rounded-xl p-2 text-char-500 hover:bg-char-50 transition-colors"
            >
              <Bell size={18} />
              {unreadCount > 0 && (
                <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-error text-[9px] font-bold text-white">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 top-full mt-2 w-80 rounded-2xl border border-char-100 bg-white shadow-lg animate-in fade-in slide-in-from-top-2">
                <div className="flex items-center justify-between border-b border-char-100 px-4 py-3">
                  <p className="text-sm font-extrabold text-char-900">
                    {language === 'ne' ? 'सूचनाहरू' : 'Notifications'}
                  </p>
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllRead}
                      className="flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary-500"
                    >
                      <CheckCheck size={14} />
                      {language === 'ne' ? 'सबै पढियो' : 'Mark all read'}
                    </button>
                  )}
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-char-400">
                      {language === 'ne' ? 'कुनै सूचना छैन' : 'No notifications'}
                    </div>
                  ) : (
                    notifications.slice(0, 10).map((n) => (
                      <div
                        key={n._id}
                        onClick={() => !n.read && handleMarkRead(n._id)}
                        className={`flex items-start gap-3 border-b border-char-50 px-4 py-3 last:border-0 cursor-pointer transition-colors ${
                          n.read ? 'bg-white hover:bg-char-50' : 'bg-primary-50 hover:bg-primary-100'
                        }`}
                      >
                        <span
                          className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${
                            n.read ? 'bg-char-300' : 'bg-primary'
                          }`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${n.read ? 'text-char-500' : 'text-char-900 font-semibold'}`}>
                            {n.message}
                          </p>
                          {n.createdAt && (
                            <p className="mt-1 text-xs text-char-400">
                              {new Date(n.createdAt).toLocaleDateString('ne-NP', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 rounded-xl bg-primary-50 px-3 py-1.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-[11px] font-extrabold text-white">
              {initials}
            </div>
            <span className="hidden text-sm font-bold text-char-800 lg:block">
              {user?.name || 'Reporter'}
            </span>
          </div>
        </div>
      </div>
    </header>
  );
};

export default ReporterTopbar;